import React, { useState, useEffect, useRef } from 'react';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import WhatsAppLoginButton from './WhatsAppLoginButton.jsx';
import Dashboard from './Dashboard';
import { apiUrl, fetchJson } from '../apiBase';
import { getMeCached, clearMeCache } from './authCache';
import { SSE_HTTP_FALLBACK_INTERVAL_MS, sseHttpFallbackDelay } from '../live/sseHttpFallbackPolicy';
import { getStoredLocale, setStoredLocale } from '../i18n/stored-locale';
import PrivacyNoticeGate from '../components/PrivacyNoticeGate';
// import { normalizePhone } from '../utils/phone';

const AdminPanel = ({ privacyNoticeManaged = false }) => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const location = useLocation();
  const normalizeUser = (u) => {
    if (!u) return u;
    const schoolId = u.school_id || u.school || u.school_name;
    return { ...u, school_id: schoolId };
  };

  // Default locale: set Turkish if not chosen yet
  useEffect(() => {
    try {
      const existing = getStoredLocale();
      if (!existing) setStoredLocale('tr-TR');
    } catch (_) {
      // ignore storage errors
    }
  }, []);

  // Check session via cookie only
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { ok, data } = await getMeCached();
        if (!cancelled && ok && data && data.admin_user) {
          setUser(normalizeUser(data.admin_user));
        }
      } catch (_) { /* ignore */ }
      finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Read query reason (session messages)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      const reason = params.get('reason');
      if (reason === 'session_expired') {
        setError('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
      } else if (reason === 'login_required') {
        setError('Bu sayfaya erişmek için giriş yapmalısınız.');
      }
    } catch (_) { /* ignore */ }
  }, [location.search]);

  const handleLogout = async () => {
    try {
      await fetchJson(apiUrl('/auth/logout'), { method: 'POST' });
    } catch (_) { /* ignore */ }
    // Cancel any pending timers/streams and reset challenge state
    try { if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; } } catch (_) {}
    try { if (esRef.current) { esRef.current.close(); esRef.current = null; } } catch (_) {}
    try { if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; } } catch (_) {}
    activeChallengeIdRef.current = null;
    autoStartedRef.current = false;
    setChallenge(null);
    setQrSvg('');
    setQrDataUrl('');
    setPolling(false);
    setSelectedSchool('');
    setSchools([]);
    resolveRequestRef.current += 1;
    setResolvingPhone(false);
    setPhone('');
  setUser(null);
  clearMeCache();
  // no localStorage usage in cookie-only mode
    setSuccess('Çıkış yapıldı.');
    setError('');
  };

  const [challenge, setChallenge] = useState(null);
  const [qrSvg, setQrSvg] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [polling, setPolling] = useState(false);
  // Phone-based school resolution
  const [phone, setPhone] = useState('');
  const [schools, setSchools] = useState([]);
  const [resolvingPhone, setResolvingPhone] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState('');
  // Refs to control single active challenge + polling loop and network
  const pollTimerRef = useRef(null);
  const qrExpiryTimerRef = useRef(null);
  const activeChallengeIdRef = useRef(null);
  const autoStartedRef = useRef(false);
  const abortRef = useRef(null);
  const esRef = useRef(null);
  const resolveRequestRef = useRef(0);

  const resetChallenge = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (qrExpiryTimerRef.current) {
      clearTimeout(qrExpiryTimerRef.current);
      qrExpiryTimerRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    activeChallengeIdRef.current = null;
    setChallenge(null);
    setQrSvg('');
    setQrDataUrl('');
    setPolling(false);
  };

  const startChallenge = async () => {
    if (!selectedSchool) {
      setError('Önce telefonunuzu doğrulayın ve okul seçin.');
      return;
    }
    setError('');
    setSuccess('');
    // Cancel previous polling if any
    resetChallenge();
    let controller;
    try {
      controller = new AbortController();
      abortRef.current = controller;
      const params = new URLSearchParams({ school_id: selectedSchool });
      const res = await fetchJson(apiUrl(`/auth/challenge/start?${params.toString()}`), {
        method: 'POST', signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (!res.ok) throw new Error('challenge_start_failed');
      const data = await res.json();
      if (controller.signal.aborted) return;
      activeChallengeIdRef.current = data.challenge_id;
      setChallenge(data);
      setQrSvg(data.wa_qr_svg || '');
      setQrDataUrl('');
      setPolling(true);
      // SSE provides prompt updates; polling also survives a stalled or closed stream.
      let completing = false;
      let consumed = false;
      const isActive = () => !controller.signal.aborted
        && activeChallengeIdRef.current === data.challenge_id;
      const closeStream = () => {
        try { esRef.current?.close(); } catch (_) {}
        esRef.current = null;
      };
      const stopWatching = (message = '') => {
        if (!isActive()) return;
        if (pollTimerRef.current) {
          clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        if (qrExpiryTimerRef.current) {
          clearTimeout(qrExpiryTimerRef.current);
          qrExpiryTimerRef.current = null;
        }
        closeStream();
        activeChallengeIdRef.current = null;
        setPolling(false);
        if (message) setError(message);
      };
      const completeLogin = async () => {
        if (!isActive() || completing) return;
        completing = true;
        try {
          if (!consumed) {
            const c = await fetchJson(apiUrl('/auth/challenge/consume'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ challenge_id: data.challenge_id }),
              signal: controller.signal,
            });
            if (!isActive()) return;
            if (!c.ok) {
              if (c.status === 400) {
                stopWatching('Bu giriş isteği artık kullanılamıyor. Yeni bir QR oluşturun.');
                return;
              }
              throw new Error('challenge_consume_failed');
            }
            consumed = true;
            closeStream();
          }
          // Once consumed, retry only the profile request, never the single-use QR.
          const me = await fetchJson(apiUrl('/admin/me'), { signal: controller.signal });
          if (!isActive()) return;
          if (!me.ok) {
            if (me.status === 401 || me.status === 403) {
              stopWatching('Tarayıcı oturumu doğrulanamadı. Yeni bir QR ile tekrar giriş yapın.');
              return;
            }
            throw new Error('profile_fetch_failed');
          }
          const mj = await me.json();
          if (!isActive()) return;
          if (!mj?.admin_user) throw new Error('profile_missing');
          clearMeCache();
          setUser(normalizeUser(mj.admin_user));
          setSuccess('Giriş başarılı!');
          setError('');
          stopWatching();
        } catch (_) {
          if (isActive()) setError('Giriş tamamlanamadı. Bağlantı yeniden deneniyor…');
        } finally {
          completing = false;
        }
      };
      const handleStatus = async (status) => {
        if (!isActive()) return;
        if (status === 'verified') {
          await completeLogin();
        } else if (status === 'expired' || status === 'not_found') {
          stopWatching('Bu giriş isteğinin süresi doldu. Yeni bir QR oluşturun.');
        } else if (status === 'consumed' && !completing && !consumed) {
          stopWatching('Bu giriş isteği zaten kullanılmış. Yeni bir QR oluşturun.');
        }
      };
      const start = Date.now();
      const ttlMs = (data.expires_in_s || 600) * 1000;
      // Expiry is a local timer, not an HTTP poll: show expired QR promptly.
      qrExpiryTimerRef.current = setTimeout(() => {
        if (isActive()) stopWatching('Bu giriş isteğinin süresi doldu. Yeni bir QR oluşturun.');
      }, ttlMs);
      const poll = async () => {
        if (!isActive()) return;
        if (Date.now() - start >= ttlMs) {
          stopWatching('Bu giriş isteğinin süresi doldu. Yeni bir QR oluşturun.');
          return;
        }
        try {
          if (consumed || completing) {
            await completeLogin();
          } else {
            const r = await fetchJson(apiUrl(`/auth/challenge/status?challenge_id=${encodeURIComponent(data.challenge_id)}`), { signal: controller.signal });
            if (!r.ok) throw new Error('challenge_status_failed');
            const s = await r.json();
            await handleStatus(s.status);
          }
        } catch (_) { /* ignore transient */ }
        if (isActive()) {
          pollTimerRef.current = setTimeout(poll, SSE_HTTP_FALLBACK_INTERVAL_MS);
        }
      };

      try {
        const sseUrl = apiUrl(`/auth/challenge/stream?challenge_id=${encodeURIComponent(data.challenge_id)}`);
        const stream = new EventSource(sseUrl, { withCredentials: false });
        esRef.current = stream;
        stream.addEventListener('status', async (ev) => {
          if (!isActive()) return;
          // Accept both JSON and the legacy Python-dict event payload.
          const match = String(ev.data || '').match(/['"]status['"]\s*:\s*['"]([^'"]+)['"]/);
          if (match) await handleStatus(match[1]);
        });
        stream.onerror = () => {
          stream.close();
          if (esRef.current === stream) esRef.current = null;
        };
      } catch (_) { /* polling remains active when SSE is unavailable */ }
      pollTimerRef.current = setTimeout(poll, sseHttpFallbackDelay(data.poll_after_ms));
    } catch (e) {
      if (!controller?.signal.aborted) setError('QR başlatılamadı.');
    }
  };

  // Ensure any stale challenge/network state is cleared when showing the login view
  useEffect(() => {
    if (!user) {
      try { if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; } } catch (_) {}
      if (qrExpiryTimerRef.current) { clearTimeout(qrExpiryTimerRef.current); qrExpiryTimerRef.current = null; }
      try { if (esRef.current) { esRef.current.close(); esRef.current = null; } } catch (_) {}
      try { if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; } } catch (_) {}
      activeChallengeIdRef.current = null;
      autoStartedRef.current = false;
      setChallenge(null);
      setQrSvg('');
      setQrDataUrl('');
      setPolling(false);
    }
  }, [user]);

  // Cleanup on unmount: stop any pending timers
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (qrExpiryTimerRef.current) {
        clearTimeout(qrExpiryTimerRef.current);
        qrExpiryTimerRef.current = null;
      }
      abortRef.current?.abort();
      abortRef.current = null;
      try { esRef.current?.close(); } catch (_) {}
      esRef.current = null;
      activeChallengeIdRef.current = null;
    };
  }, []);

  // Client-side QR fallback if server didn't embed SVG
  useEffect(() => {
    const gen = async () => {
      if (challenge && !qrSvg && challenge.wa_deeplink && !qrDataUrl) {
        try {
          const url = await QRCode.toDataURL(challenge.wa_deeplink, { width: 220, margin: 1 });
          setQrDataUrl(url);
        } catch (_) {
          // ignore
        }
      }
    };
    gen();
  }, [challenge, qrSvg, qrDataUrl]);

  // Resolve school from phone
  const resolvePhone = async () => {
    const requestId = ++resolveRequestRef.current;
    setError('');
    setSuccess('');
    resetChallenge();
    setSchools([]);
    setSelectedSchool('');
    setResolvingPhone(true);
    try {
      const res = await fetchJson(apiUrl('/auth/resolve'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) throw new Error('resolve_failed');
      const data = await res.json();
      if (requestId !== resolveRequestRef.current) return;
      const uniqueSchools = Array.from(new Map((data?.schools || [])
        .filter((item) => item.school_id != null)
        .map((item) => [String(item.school_id), item])).values());
      setSchools(uniqueSchools);
      if (uniqueSchools.length === 1) setSelectedSchool(String(uniqueSchools[0].school_id));
      if (uniqueSchools.length === 0) setError('Bu telefon numarasına bağlı bir okul bulunamadı.');
    } catch (_) {
      if (requestId === resolveRequestRef.current) setError('Okullar alınamadı. Lütfen tekrar deneyin.');
    } finally {
      if (requestId === resolveRequestRef.current) setResolvingPhone(false);
    }
  };

  const handlePhoneChange = (event) => {
    resolveRequestRef.current += 1;
    setPhone(event.target.value);
    setSchools([]);
    setSelectedSchool('');
    setResolvingPhone(false);
    setError('');
    resetChallenge();
  };

  const handleSchoolChange = (event) => {
    setSelectedSchool(event.target.value);
    resetChallenge();
    setError('');
  };

  if (checking) {
    return null; // avoid flashing the login view while validating session
  }

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 600 }}>
            Okul Yönetici Paneli
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Telefon numaranızı girip okulunuzu seçin. Ardından WhatsApp ile giriş yapın veya QR’ı okutun.
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Phone input to resolve school */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel shrink htmlFor="phone-input">Telefon</InputLabel>
              <input
                id="phone-input"
                style={{ height: 40, padding: '0 12px', border: '1px solid #ccc', borderRadius: 6 }}
                placeholder="Telefon numarası"
                value={phone}
                onChange={handlePhoneChange}
              />
            </FormControl>
            <Button variant="outlined" onClick={resolvePhone} disabled={!phone.trim() || resolvingPhone} sx={{ height: 40, alignSelf: 'center' }}>
              {resolvingPhone ? 'Aranıyor…' : 'Okulları Bul'}
            </Button>
          </Box>

          {schools.length > 1 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="login-school-label">Giriş yapılacak okul</InputLabel>
              <Select
                labelId="login-school-label"
                label="Giriş yapılacak okul"
                value={selectedSchool}
                onChange={handleSchoolChange}
              >
                {schools.map((school) => (
                  <MenuItem key={school.school_id} value={String(school.school_id)}>
                    {school.school_name || `Okul ${school.school_id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {schools.length === 1 && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Okul: {schools[0].school_name || `Okul ${schools[0].school_id}`}
            </Typography>
          )}

          {/* Option-1: WhatsApp ile giriş (Mesaj gönder) */}
          <Box sx={{ mb: 2 }}>
            <WhatsAppLoginButton schoolId={selectedSchool} disabled={!selectedSchool} />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Option-3: QR ile Giriş (masaüstü) */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {!challenge && (
              <Button variant="outlined" onClick={startChallenge} disabled={!selectedSchool}>QR ile Giriş Başlat</Button>
            )}
            {challenge && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>WhatsApp ile giriş için QR’ı okutun veya butona dokunun.</Typography>
                {qrSvg ? (
                  <Box sx={{ border: '1px solid #E0E0E0', p: 1, borderRadius: 1, mb: 1 }} dangerouslySetInnerHTML={{ __html: qrSvg }} />
                ) : qrDataUrl ? (
                  <Box sx={{ border: '1px solid #E0E0E0', p: 1, borderRadius: 1, mb: 1 }}>
                    <Box component="img" src={qrDataUrl} alt="QR" sx={{ width: 220, height: 220 }} />
                  </Box>
                ) : (
                  <Box sx={{ my: 2 }}><CircularProgress size={24} /></Box>
                )}
                <Box sx={{ mb: 1 }}>
                  <Button component="a" href={challenge.wa_deeplink} target="_blank">WhatsApp’ı Aç</Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {polling ? 'WhatsApp mesajınızı bekliyoruz…' : 'Beklemede'}
                </Typography>
                {polling && <Box sx={{ mt: 1 }}><CircularProgress size={20} /></Box>}
                {!polling && <Button onClick={startChallenge}>Yeni QR Oluştur</Button>}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Telefon ile giriş kaldırıldı */}
        </Paper>
      </Container>
    );
  }

  const dashboard = <Dashboard user={user} onLogout={handleLogout} />;
  return privacyNoticeManaged ? dashboard : <PrivacyNoticeGate>{dashboard}</PrivacyNoticeGate>;
};

export default AdminPanel;
